import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Package, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useMe } from '~/api/auth';
import { isApiError } from '~/api/client';
import {
  SPACE_ROLE_LABEL,
  type SpaceSummary,
  spacesQueryOptions,
  trackerHomeQueryOptions,
  useCreateSpace,
} from '~/api/orders';
import { shortDateTime } from '~/features/orders/format';
import { Button } from '~/ui/Button';
import { Dialog } from '~/ui/Dialog';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Input } from '~/ui/Input';
import { Notice } from '~/ui/Notice';
import { Skeleton } from '~/ui/Skeleton';
import { Hint } from '~/ui/Hint';

export const Route = createFileRoute('/_app/orders/')({
  component: TrackerHome,
});

/** Steps shown before "Show all"; the soonest due come first. */
const WAITING_SHOWN = 8;
const BAR = ['bg-accent-deep', 'bg-accent', 'bg-accent/60', 'bg-accent/35', 'bg-accent/20'];

/**
 * The Order Tracker's front door: the steps waiting on me in every workspace,
 * then each workspace with its counts and where its open orders are.
 */
function TrackerHome() {
  const me = useMe();
  const tz = me.person.tz ?? me.workspace.tz;
  const spaces = useQuery(spacesQueryOptions);
  const home = useQuery(trackerHomeQueryOptions);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [allWaiting, setAllWaiting] = useState(false);

  if (spaces.isPending) {
    return (
      <div className="tracker -mx-4 -mt-6 min-h-(--main-h) px-4 pt-6 md:-mx-8 md:px-8">
        <Skeleton lines={6} />
      </div>
    );
  }
  if (spaces.isError) return <ErrorNotice error={spaces.error} onRetry={() => spaces.refetch()} />;
  const { spaces: list, canCreate } = spaces.data;
  const live = list.filter((s) => !s.archivedAt);
  const waiting = home.data?.waiting ?? [];
  if (live.length === 1 && !canCreate && home.isSuccess && waiting.length === 0) {
    return <Navigate to="/orders/$spaceId" params={{ spaceId: live[0]!.id }} replace />;
  }
  const summaries = new Map((home.data?.summaries ?? []).map((s) => [s.spaceId, s]));

  return (
    <div className="tracker -mx-4 -mt-6 min-h-(--main-h) px-4 pt-8 pb-12 md:-mx-8 md:px-10">
      <div className="mx-auto flex max-w-[1180px] flex-col gap-7">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs tracking-[0.12em] text-accent uppercase">
              Order Tracker
            </p>
            <h1 className="mt-1 text-[30px] leading-tight">Workspaces</h1>
            <p className="mt-1 max-w-xl text-sm text-ink-2">
              Each workspace has its own orders, people, order template and pipeline.
            </p>
          </div>
          {canCreate && (
            <Button icon={<Plus size={16} aria-hidden />} onClick={() => setCreating(true)}>
              New workspace
            </Button>
          )}
        </header>

        {waiting.length > 0 && (
          <section
            data-tour="orders-waiting"
            aria-labelledby="waiting-h"
            className="rounded-panel border border-line bg-paper px-5 py-4"
          >
            <div className="mb-1 flex flex-wrap items-baseline gap-x-3">
              <h2 id="waiting-h" className="text-base font-semibold">
                Waiting on you
              </h2>
              <span className="text-sm text-ink-3">
                {waiting.length === 1 ? 'one step' : `${waiting.length} steps`} you are on, in every
                workspace
              </span>
            </div>
            <ul className="flex flex-col">
              {(allWaiting ? waiting : waiting.slice(0, WAITING_SHOWN)).map((w) => (
                <li key={w.stepId} className="border-t border-line/70 first:border-t-0">
                  <Link
                    to="/orders/$spaceId/$orderId"
                    params={{ spaceId: w.spaceId, orderId: w.orderId }}
                    className="flex items-start gap-3 py-2.5 hover:text-accent-ink sm:items-center"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-baseline gap-2 text-sm">
                        <span className="shrink-0 font-mono text-xs text-accent-ink">{w.ref}</span>
                        <Hint label={w.stepName}>
                          <span className="truncate font-medium">{w.stepName}</span>
                        </Hint>
                      </span>
                      <Hint label={`${w.orderTitle} · ${w.spaceName}`}>
                        <span className="mt-0.5 line-clamp-2 text-[13px] text-ink-3 [overflow-wrap:anywhere]">
                          {w.orderTitle} · {w.spaceName}
                        </span>
                      </Hint>
                      {/* On a phone the due date has a line of its own, so the stage name has room. */}
                      <span
                        className={`mt-0.5 block text-xs sm:hidden ${w.overdue ? 'font-medium text-late' : 'text-ink-2'}`}
                      >
                        {w.dueAt
                          ? `${w.overdue ? 'Overdue, was due' : 'Due'} ${shortDateTime(w.dueAt, tz)}`
                          : w.status === 'in_progress'
                            ? 'In progress'
                            : 'No due date'}
                      </span>
                    </span>
                    <span
                      className={`hidden shrink-0 text-right text-[13px] sm:block ${w.overdue ? 'font-medium text-late' : 'text-ink-2'}`}
                    >
                      {w.dueAt
                        ? `${w.overdue ? 'Overdue, was due' : 'Due'} ${shortDateTime(w.dueAt, tz)}`
                        : w.status === 'in_progress'
                          ? 'In progress'
                          : 'No due date'}
                    </span>
                    <ArrowRight
                      size={14}
                      aria-hidden
                      className="mt-1 shrink-0 text-ink-3 sm:mt-0"
                    />
                  </Link>
                </li>
              ))}
            </ul>
            {waiting.length > WAITING_SHOWN && (
              <button
                type="button"
                onClick={() => setAllWaiting((x) => !x)}
                className="mt-1 rounded-control px-1 py-1 text-sm text-accent-ink hover:underline"
              >
                {allWaiting ? 'Show fewer' : `Show all ${waiting.length}`}
              </button>
            )}
          </section>
        )}

        {live.length === 0 ? (
          <EmptyState
            icon={<Package aria-hidden />}
            title={canCreate ? 'No workspaces yet.' : 'You are not in a workspace yet.'}
            body={
              canCreate
                ? 'Start one for your store; the order fields and a standard pipeline are ready to use.'
                : 'Ask your manager to add you to one.'
            }
            action={
              canCreate ? (
                <Button size="sm" onClick={() => setCreating(true)}>
                  Start a workspace
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul data-tour="orders-spaces" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {live.map((s) => (
              <li key={s.id} className="min-w-0">
                <SpaceCard
                  id={s.id}
                  name={s.name}
                  code={s.code}
                  role={s.myRoleName ?? SPACE_ROLE_LABEL[s.myRole]}
                  people={s.memberCount}
                  summary={summaries.get(s.id)}
                  fallbackOpen={s.openOrders}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      <CreateSpace
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => void navigate({ to: '/orders/$spaceId', params: { spaceId: id } })}
      />
    </div>
  );
}

function SpaceCard({
  id,
  name,
  code,
  role,
  people,
  summary,
  fallbackOpen,
}: {
  id: string;
  name: string;
  code: string;
  role: string;
  people: number;
  summary: SpaceSummary | undefined;
  fallbackOpen: number;
}) {
  const c = summary?.counts;
  const total = (summary?.byStep ?? []).reduce((n, b) => n + b.count, 0);
  return (
    <Link
      to="/orders/$spaceId"
      params={{ spaceId: id }}
      className="flex h-full flex-col gap-4 rounded-panel border border-line bg-paper p-5 shadow-card transition-colors hover:border-accent/60"
    >
      <span className="flex items-center gap-3">
        <span className="grid h-11 min-w-11 shrink-0 place-items-center rounded-xl bg-accent px-1.5 font-mono text-[11px] font-semibold text-on-accent">
          {code}
        </span>
        <span className="min-w-0 flex-1">
          <Hint label={name}>
            <span className="block truncate text-base font-semibold">{name}</span>
          </Hint>
          <span className="block text-[13px] text-ink-3">
            {role} · {people} {people === 1 ? 'person' : 'people'}
          </span>
        </span>
      </span>
      <span className="grid grid-cols-3 gap-2">
        <Count n={c?.active ?? fallbackOpen} label="open" />
        <Count n={c?.dueToday} label="due today" />
        <Count n={c?.overdue} label="overdue" late={(c?.overdue ?? 0) > 0} />
      </span>
      {total > 0 && (
        <span className="mt-auto block">
          <span className="mb-1.5 block text-xs text-ink-3">Open orders by step</span>
          <span className="flex h-2 gap-0.5 overflow-hidden rounded-full">
            {summary!.byStep.map((b, i) => (
              <Hint key={b.key} label={`${b.name}: ${b.count}`}>
                <span className={BAR[i % BAR.length]} style={{ flexGrow: b.count, flexBasis: 0 }} />
              </Hint>
            ))}
          </span>
          <span className="mt-1.5 block truncate text-xs text-ink-3">
            {summary!.byStep
              .slice(0, 3)
              .map((b) => `${b.name} ${b.count}`)
              .join(' · ')}
            {summary!.byStep.length > 3 ? ' …' : ''}
          </span>
        </span>
      )}
    </Link>
  );
}

function Count({ n, label, late }: { n: number | undefined; label: string; late?: boolean }) {
  return (
    <span className={`rounded-control px-3 py-2 ${late ? 'bg-late/8' : 'bg-paper-2/70'}`}>
      <span
        className={`block font-mono text-lg leading-tight font-medium tabular ${late ? 'text-late' : ''}`}
      >
        {n ?? '–'}
      </span>
      <span className={`block text-xs ${late ? 'text-late' : 'text-ink-3'}`}>{label}</span>
    </span>
  );
}

function CreateSpace({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const create = useCreateSpace();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const error = create.error && isApiError(create.error) ? create.error : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const res = await create.mutateAsync({
        name: name.trim(),
        ...(code.trim() ? { code: code.trim().toUpperCase() } : {}),
      });
      setName('');
      setCode('');
      onCreated(res.space.id);
    } catch {
      // shown below
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New workspace"
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-space"
            loading={create.isPending}
            disabled={name.trim().length < 2}
          >
            Create space
          </Button>
        </>
      }
    >
      <form id="new-space" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Khushbu Jewels — Orders"
          autoFocus
        />
        <Input
          label="Order code"
          mono
          optional
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          hint="Orders are numbered CODE-1, CODE-2… Made from the name if empty."
          maxLength={6}
        />
        <p className="text-xs text-ink-3">
          It starts with an "Order details" template and a 5-step "Standard" pipeline; change both
          in its settings.
        </p>
        {error && <Notice tone="late">{error.message}</Notice>}
      </form>
    </Dialog>
  );
}
